// ReactionForm.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Modal,
  Alert,
} from '@mui/material';
import { Formik, Form, Field, type FormikProps, type FieldProps } from 'formik';
import * as Yup from 'yup';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import JsonForm, { JsonFormTemplate } from '../../common/JsonForm';
// import { useCustomer } from '../../../contexts/CustomerContext';
import { ReactionToCustomer, ReactionType } from '../../../types/reaction';
import { useAuthStore } from '../../../store/useAuthStore3';
import { api } from '../../../api/reactionApi2';

export interface ReactionFormProps {
  reactionToCustomer: ReactionToCustomer | null;
  onSave: (values: ReactionToCustomer) => Promise<void> | void;  
  onCancel: () => void;
  // onTest: () => void;
  reactionTypes: ReactionType[];
}

export const SelectByBool = (v: boolean, t: string, f: string, none: string) => {
  if (v === true) return t;
  if (v === false) return f;
  return none;
}

const ReactionForm: React.FC<ReactionFormProps> = ({
  reactionToCustomer,
  onSave,
  onCancel,
  reactionTypes,
  // onTest,
}) => {
  const { t } = useTranslation();
  const title = reactionToCustomer ? t('ReactionForm.Edit_Reaction') : t('ReactionForm.Add_New_Reaction');
  const { customerId } = useAuthStore();
  const [isCredentialsModalOpen, setIsCredentialsModalOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [testMeessage, setTestMeessage] = useState<string | null>(null);
  const [testResultVisible, setTestResultVisible] = useState<boolean>(false);

  const defaultCredentialsEditorTemplate: JsonFormTemplate = {
    fields: [
      {
        name: 'jsonString',
        label: t('ReactionForm.Credentials_JSON'),
        type: 'json',
        validation: { isJson: true },
        muiProps: {
          multiline: true,
          rows: 10,
          placeholder: t('ReactionForm.JSON_Placeholder'),
        },
      },
    ],
  };

  const initialValues: ReactionToCustomer = reactionToCustomer || {
    customer_id: customerId || -1,
    reaction_type_id: -1,
    param: [],
    name: '',
    id: -1,
  };

  const validationSchema = Yup.object({
    reaction_type_id: Yup.number().required(('Reaction_type_is_required')),
    param: Yup.mixed().required(('param_is_required')),
  });

  if (customerId === -1) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>{('Loading_form_data')}</Typography>
      </Box>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => { // Додаємо async
        // Очищаємо попередню помилку
        setSaveError(null); 
        try {
          await Promise.resolve(onSave(values)); 
        } catch (error) {
          console.error("Помилка збереження ReactionToCustomer:", error);
          const errorMessage = error instanceof Error ? error.message : t('OriginForm.Unknown_Save_Error');
          setSaveError(t('OriginForm.Save_Failed', { error: errorMessage }));
          setSubmitting(false); 
        }
      }}      
      enableReinitialize
    >
      {(formikProps: FormikProps<ReactionToCustomer>) => {
        const { values, setFieldValue } = formikProps;

        const selectedReactionType = reactionTypes.find(
          (rt) => rt.reaction_type_id === values.reaction_type_id
        );

        const onTest = async () => {
          setTestResultVisible(true);
          setTestMeessage('testing...');
          const {reaction_type_id, param} = values;
          const test = await api.handleTest(reaction_type_id, param);
          setTestMeessage(test.message);
        };

        const currentCredentialsTemplate: JsonFormTemplate =
          selectedReactionType?.template || defaultCredentialsEditorTemplate;

        return (
          <Form>
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>

            {saveError && (
              <Alert 
                severity="error" 
                sx={{ mb: 2 }}
                onClose={() => setSaveError(null)} // Дозволяємо закрити повідомлення
              >
                {saveError}
              </Alert>
            )}
            
            <Stack spacing={2} sx={{ mt: 2 }}>

              <Field name="reaction_type_id">
                {({ field, meta }: { field: any; meta: any }) => (
                  <FormControl fullWidth error={meta.touched && !!meta.error}>
                    <InputLabel id="origin-type-id-label">{t('ReationForm.Reation_Type_Label')}</InputLabel>
                    <Select labelId="origin-type-id-label" label={t('ReationForm.Reation_Type_Label')} {...field}>
                      <MenuItem value=""><em>{t('ReactionForm.Select_an_Reaction_Type')}</em></MenuItem>
                      {reactionTypes.map((type) => (
                        <MenuItem key={type.reaction_type_id} value={type.reaction_type_id}>
                          {type.description}
                        </MenuItem>
                      ))}
                    </Select>
                    {meta.touched && meta.error && (
                      <Typography color="error" variant="caption" component="div">{meta.error}</Typography>
                    )}
                  </FormControl>
                )}
              </Field>

              <Field name="name">
                {({ field, meta }: { field: any; meta: any }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('ReactionForm.Name_Label')}
                    error={meta.touched && !!meta.error}
                    helperText={(meta.touched && meta.error) || t('ReactionForm.Name_Helper_Text')}
                  />
                )}
              </Field>

              <Field name="credentials">
                {({ field: fieldPropsForTextField, meta }: FieldProps<string | undefined, ReactionToCustomer>) => (
                  <div>
                    <TextField
                      {...fieldPropsForTextField}
                      fullWidth
                      label={t('ReactionForm.Parameters_Label')}
                      multiline
                      rows={3}
                      error={meta.touched && !!meta.error}
                      helperText={(meta.touched && meta.error) || t('OriginForm.Credentials_Helper_Text')}
                      value={JSON.stringify(values.param, null, 2)}
                      onChange={(e) => setFieldValue("param", JSON.parse(e.target.value))}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setIsCredentialsModalOpen(true)}
                        startIcon={<EditIcon />}
                      >
                        {t('OriginForm.Edit_JSON_Button')}
                      </Button>
                    </Box>
                  </div>
                )}
              </Field>

            </Stack>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button onClick={onTest} sx={{ mr: 1 }} disabled={formikProps.isSubmitting} variant='outlined'>
                  {t('ReactionForm.Test_Reaction')}
                </Button>
                <Button onClick={onCancel} sx={{ mr: 1 }} disabled={formikProps.isSubmitting} variant='outlined'>
                  {t('OriginForm.Cancel')}
                </Button>
                <Button type="submit" variant="contained" disabled={formikProps.isSubmitting || !formikProps.isValid || !formikProps.dirty}>
                  {formikProps.isSubmitting ? t('OriginForm.Saving') : t('OriginForm.Save_Origin')}
                </Button>
              </Box>
            </Box>

                        {testResultVisible && (
                        <Box sx={{ mt: 2 }}>
                            <TextField
                                fullWidth
                                label={t('OriginForm.Test_Result_Label')}
                                multiline
                                rows={3}
                                value={testMeessage}
                                InputProps={{
                                    readOnly: true,
                                }}
                            />
                        </Box>
                        )}

            {isCredentialsModalOpen && (
              <Modal
                open={isCredentialsModalOpen}
                onClose={() => setIsCredentialsModalOpen(false)}
                aria-labelledby="credentials-json-editor-title"
              >
                <Box sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: { xs: '90%', sm: 600 },
                  bgcolor: 'background.paper',
                  boxShadow: 24,
                  p: { xs: 2, sm: 4 },
                  borderRadius: 1,
                  maxHeight: '90vh',
                  overflowY: 'auto',
                }}
                >
                  {(() => {
                    let jsonFormInitialData: Record<string, any>;
                    const isSingleJsonStringFieldTemplate =
                      currentCredentialsTemplate.fields.length === 1 &&
                      currentCredentialsTemplate.fields[0].type === 'json';

                    if (isSingleJsonStringFieldTemplate) {
                      const fieldName = currentCredentialsTemplate.fields[0].name;
                      jsonFormInitialData = { [fieldName]: values.param || '{}' };
                    } else {
                      try {
                        jsonFormInitialData = values.param || '{}';
                        if (typeof jsonFormInitialData !== 'object' || jsonFormInitialData === null) {
                          jsonFormInitialData = {};
                        }
                      } catch (e) {
                        console.warn("[OriginForm] Credentials string is not valid JSON. Initializing JsonForm with empty data.", e);
                        jsonFormInitialData = {};
                      }
                    }

                    return (
                      <JsonForm
                        template={currentCredentialsTemplate}
                        initialData={jsonFormInitialData}
                        onSubmit={(data) => {
                          setFieldValue('param', data);
                          setIsCredentialsModalOpen(false);
                        //   return;


                        //   // ???
                        //   let finalCredentialsString: string;

                        //   const wasSingleJsonStringFieldTemplate =
                        //     currentCredentialsTemplate.fields.length === 1 &&
                        //     currentCredentialsTemplate.fields[0].type === 'json' &&
                        //     data.hasOwnProperty(currentCredentialsTemplate.fields[0].name) &&
                        //     typeof data[currentCredentialsTemplate.fields[0].name] === 'string';

                        //   if (wasSingleJsonStringFieldTemplate) {
                        //     finalCredentialsString = data[currentCredentialsTemplate.fields[0].name] as string;
                        //   } else {
                        //     finalCredentialsString = JSON.stringify(data);
                        //   }

                        //   if (finalCredentialsString.trim() === '') {
                        //     finalCredentialsString = '{}';
                        //   }

                        //   try {
                        //     JSON.parse(finalCredentialsString);
                        //   } catch (e) {
                        //     console.warn("[OriginForm] Produced invalid JSON for credentials after JsonForm submission. Defaulting to '{}'. String was:", finalCredentialsString);
                        //     finalCredentialsString = '{}';
                        //   }

                        //   setFieldValue('credentials', finalCredentialsString);
                        //   setIsCredentialsModalOpen(false);
                        }}
                        onCancel={() => setIsCredentialsModalOpen(false)}
                        title={t('ReactionForm.Edit_Parameters_JSON_Title')}
                        submitButtonText={t('OriginForm.Apply_JSON_Button')}
                        cancelButtonText={t('OriginForm.Cancel')}
                      />
                    );
                  })()}
                </Box>
              </Modal>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default ReactionForm;